// =============================================================================
// DAILY MEMORY PROMPT
// =============================================================================
// Proactive once-a-day nudge: if any family thread has an "On this day" memory,
// gently offer to look back together over a co-view. The signature reopen loop.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../../api/client.js';
import { useChatStore } from '../../stores/chatStore.js';
import { useCallStore } from '../../stores/callStore.js';
import { useMemorySessionStore } from '../../stores/memorySessionStore.js';
import { X, Images } from '../ui/Icons.js';

// Bound the cold-start scan: check at most this many threads for memories so a
// large family does not fan out a request per conversation on every load.
const MAX_THREADS_SCANNED = 15;

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

interface DailyPrompt {
  conversationId: string;
  conversationName: string | null;
  count: number;
}

export function DailyMemoryPrompt() {
  const { t } = useTranslation();
  const conversations = useChatStore((s) => s.conversations);
  const fetchConversations = useChatStore((s) => s.fetchConversations);
  const activeCall = useCallStore((s) => s.activeCall);
  const launchMemoryFromDate = useMemorySessionStore((s) => s.launchMemoryFromDate);

  const [prompt, setPrompt] = useState<DailyPrompt | null>(null);

  useEffect(() => {
    let cancelled = false;

    const storageKey = `chlann.memoryPrompt.shown.${todayKey()}`;
    if (localStorage.getItem(storageKey)) return;

    void (async () => {
      let threads = conversations;
      if (threads.length === 0) {
        await fetchConversations();
        if (cancelled) return;
        threads = useChatStore.getState().conversations;
      }

      for (const conv of threads.slice(0, MAX_THREADS_SCANNED)) {
        if (cancelled) return;
        const result = await api.getMemories(conv.id);
        const memories = result.data?.memories;
        if (memories && memories.length > 0) {
          localStorage.setItem(storageKey, '1');
          setPrompt({
            conversationId: conv.id,
            conversationName: conv.name,
            count: memories.length,
          });
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Run once on mount; conversations/fetch are read fresh inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Never surface over a live call or once it has been answered.
  if (!prompt || activeCall) return null;

  const handleLookTogether = () => {
    void launchMemoryFromDate(prompt.conversationId, new Date().toISOString());
    setPrompt(null);
  };

  const body = prompt.conversationName
    ? t('memory.dailyPromptBodyNamed', 'You and {{name}} shared moments on this day.', {
        name: prompt.conversationName,
      })
    : t('memory.dailyPromptBody', 'Your family shared moments on this day.');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-4 left-1/2 z-40 w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-primary/30 bg-surface-2 p-4 shadow-2xl"
        role="dialog"
        aria-label={t('memory.dailyPromptTitle', 'On this day')}
      >
        <button
          type="button"
          onClick={() => { setPrompt(null); }}
          className="absolute right-2 top-2 rounded-full p-1.5 text-ink-3 transition-colors hover:bg-surface-3/60"
          aria-label={t('common.dismiss', 'Dismiss')}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Images className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="font-display text-sm font-semibold text-ink-1">
              {t('memory.dailyPromptTitle', 'On this day')}
            </p>
            <p className="mt-0.5 text-sm text-ink-3">{body}</p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => { setPrompt(null); }}
            className="min-h-[44px] rounded-lg px-3 py-2 text-sm font-medium text-ink-3 transition-colors hover:bg-surface-3/60"
          >
            {t('memory.notNow', 'Not now')}
          </button>
          <button
            type="button"
            onClick={handleLookTogether}
            className="min-h-[44px] rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-on transition-all hover:bg-primary-hover active:scale-95"
          >
            {t('memory.lookTogether', 'Look back together')}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
